import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import schoolsCsv from '../data/schools_utf_8.csv';
import { getUserDisplayName } from '../api/auth';
import { membersAPI } from '../api/members';
import { useAuth } from '../context/AuthContext';

type SchoolRecord = {
  code: string;
  schoolName: string;
  location: string;
};

type StudentEditForm = {
  tauserId: string;
  email: string;
  name: string;
  password: string;
  academyname: string;
  phone: string;
  parentPhone: string;
  class: string;
  memo: string;
  location: string;
  schoolName: string;
  schoolCode: string;
};

const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const parseCsv = (csvText: string): SchoolRecord[] => {
  return csvText
    .split(/\r?\n/)
    .slice(1)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [code = '', schoolName = '', location = ''] = line.split(',');
      return {
        code: code.trim(),
        schoolName: schoolName.trim(),
        location: location.trim(),
      };
    })
    .filter((item) => item.code && item.schoolName && item.location);
};

const getTauserId = (user: Record<string, unknown> | null): string => {
  const rawId = user?.tauser_id ?? user?.tauserId ?? user?.id;

  if (typeof rawId === 'number') {
    return String(rawId);
  }

  return normalizeText(rawId);
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const toStudentEditForm = (user: Record<string, unknown> | null): StudentEditForm => {
  return {
    tauserId: getTauserId(user),
    email: normalizeText(user?.email),
    name:
      normalizeText(user?.name) ||
      normalizeText(user?.username) ||
      normalizeText(user?.email) ||
      '학생',
    password: '',
    academyname:
      normalizeText(user?.academyname) ||
      normalizeText(user?.academyName) ||
      normalizeText(user?.academy),
    phone: normalizeText(user?.phone),
    parentPhone: normalizeText(user?.parentPhone || user?.parent_phone),
    class: normalizeText(user?.class),
    memo: normalizeText(user?.memo),
    location: normalizeText(user?.location),
    schoolName: normalizeText(user?.schoolName),
    schoolCode: normalizeText(user?.schoolCode),
  };
};

// 시간 문자열을 오전/오후 형식으로 변환
const formatTimeToAmPm = (timeStr: string): string => {
  if (!timeStr || timeStr.trim().length === 0) {
    return '';
  }

  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return timeStr;
  }

  const hour = parseInt(match[1], 10);
  const minute = match[2];

  const period = hour >= 12 ? '오후' : '오전';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;

  return `${period} ${String(displayHour).padStart(2, '0')}시 ${minute}분`;
};

// eventday 타임스탐프를 파싱
const parseEventdayTimestamp = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  const millis = parsed < 1_000_000_000_000 ? parsed * 1000 : parsed;
  const date = new Date(millis);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return millis;
};

const isTodayOrFutureTimestamp = (eventdayMs: number | null): boolean => {
  if (!eventdayMs) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventdayMs >= today.getTime();
};

// 보강 일정 포맷팅: "월 / 일 (요일) 오전/오후 HH:MM"
const formatRemedialSchedule = (eventdayMs: number | null): string => {
  if (!eventdayMs) {
    return '';
  }

  const date = new Date(eventdayMs);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = date.getDay();

  const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];
  const dayLabel = dayLabels[dayOfWeek] || '요일';

  const hour = date.getHours();
  const minute = String(date.getMinutes()).padStart(2, '0');

  const period = hour >= 12 ? '오후' : '오전';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;

  return `${month}/${day} (${dayLabel}) ${period} ${String(displayHour).padStart(2, '0')}시 ${minute}분`;
};

const HomePage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [studentDetail, setStudentDetail] = useState<Record<string, unknown> | null>(null);
  const [editForm, setEditForm] = useState<StudentEditForm>(() => toStudentEditForm(user as Record<string, unknown> | null));
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pageMessage, setPageMessage] = useState('');

  const profileSource = (studentDetail || user || null) as Record<string, unknown> | null;

  const displayName = getUserDisplayName(profileSource);

  // 주간 일정 
  const weekSchedule = useMemo(() => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];

    return days.map((dayKey, index) => {
      const rawValue = (profileSource as any)?.[dayKey];
      const scheduleValue = normalizeText(rawValue || '');
      const formattedTime = scheduleValue ? formatTimeToAmPm(scheduleValue) : '';
      return {
        label: dayLabels[index],
        time: formattedTime,
      };
    });
  }, [profileSource]);

  const remedialEventdayMs = useMemo(
    () => parseEventdayTimestamp((profileSource as any)?.eventday),
    [profileSource]
  );

  // 보강 일정: 오늘 이후(오늘 포함)만 노출
  const remedialSchedule = useMemo(() => {
    if (!isTodayOrFutureTimestamp(remedialEventdayMs)) {
      return '';
    }

    return formatRemedialSchedule(remedialEventdayMs);
  }, [remedialEventdayMs]);

  const hasRemedialSchedule = remedialSchedule.length > 0;

  useEffect(() => {
    const tauserId = getTauserId(user as Record<string, unknown> | null);
    if (!tauserId) {
      setStudentDetail(null);
      return;
    }

    let isCancelled = false;

    const loadStudentDetail = async () => {
      try {
        const response = await membersAPI.getTauserById(tauserId);
        if (isCancelled) {
          return;
        }

        const detail = (response?.data || null) as Record<string, unknown> | null;
        setStudentDetail(detail);
      } catch {
        if (!isCancelled) {
          setStudentDetail(null);
        }
      }
    };

    loadStudentDetail().catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [user]);

  useEffect(() => {
    setEditForm((current) => ({
      ...toStudentEditForm(profileSource),
      password: current.password,
    }));
  }, [profileSource]);

  useEffect(() => {
    const loadSchools = async () => {
      const response = await fetch(schoolsCsv);
      const csvText = await response.text();
      setSchools(parseCsv(csvText));
    };

    loadSchools().catch(() => undefined);
  }, []);

  const locations = useMemo(
    () => Array.from(new Set(schools.map((school) => school.location))).sort((a, b) => a.localeCompare(b, 'ko')),
    [schools]
  );

  const filteredSchools = useMemo(
    () =>
      schools
        .filter((school) => school.location === (editForm.location || ''))
        .sort((a, b) => a.schoolName.localeCompare(b.schoolName, 'ko')),
    [editForm.location, schools]
  );

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handlePasswordFieldClick = () => {
    if (isPasswordEditing) {
      return;
    }

    const confirmed = window.confirm('비밀번호를 수정합니까?');
    if (!confirmed) {
      return;
    }

    setIsPasswordEditing(true);
  };

  const handleFieldChange = (field: keyof StudentEditForm, value: string) => {
    setEditForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleStudentLocationChange = (nextLocation: string) => {
    setEditForm((current) => ({
      ...current,
      location: nextLocation,
      schoolName: '',
      schoolCode: '',
    }));
  };

  const handleStudentSchoolChange = (nextSchoolCode: string) => {
    const selectedSchool = filteredSchools.find((school) => school.code === nextSchoolCode);
    setEditForm((current) => ({
      ...current,
      schoolCode: nextSchoolCode,
      schoolName: selectedSchool?.schoolName || '',
    }));
  };

  const handleSave = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!editForm.tauserId) {
      setPageMessage('내 회원 tauser_id를 찾을 수 없습니다. 다시 로그인해 주세요.');
      return;
    }

    const payload: Record<string, unknown> = {
      role: 'student',
      name: editForm.name,
      academyname: editForm.academyname,
      phone: editForm.phone,
      parentPhone: editForm.parentPhone,
      parent_phone: editForm.parentPhone,
      class: editForm.class,
      memo: editForm.memo,
      location: editForm.location,
      schoolName: editForm.schoolName,
      schoolCode: editForm.schoolCode,
      uniqueKey: `${editForm.phone}_${editForm.name}`,
    };

    const nextPassword = editForm.password.trim();
    if (nextPassword) {
      payload.password = nextPassword;
    }

    setIsSaving(true);
    setPageMessage('');

    try {
      await membersAPI.updateTauser(editForm.tauserId, payload);
      const [, refreshedDetailResponse] = await Promise.all([
        refreshUser(),
        membersAPI.getTauserById(editForm.tauserId),
      ]);

      setStudentDetail((refreshedDetailResponse?.data || null) as Record<string, unknown> | null);
      setPageMessage('내 정보가 수정되었습니다.');
      setEditForm((current) => ({
        ...current,
        password: '',
      }));
      setIsPasswordEditing(false);
    } catch (error: any) {
      setPageMessage(`정보 수정 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsSaving(false);
    }
  }, [editForm, refreshUser]);

  return (
    <main className="auth-page">
      <section className="auth-card admin-card">
        <div className="admin-header-row">
          <div>
            <p className="auth-eyebrow">STUDENT HOME</p>
            <h1>{displayName}님, 반갑습니다</h1>
            <p className="auth-description">내 정보 수정 화면입니다.</p>
          </div>
          <div className="admin-action-row">
            <button type="button" className="auth-secondary-button admin-logout-button" onClick={handleLogout}>
              로그아웃
            </button>
          </div>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-item">
            <span>학생명</span>
            <strong>{displayName || '-'}</strong>
          </div>
        </div>

        <div className="student-schedule-card">
          <h3>시간표</h3>
          <div className="student-week-schedule">
            {weekSchedule.map((day, index) => (
              <div key={index} className={day.time ? 'student-week-schedule-item active' : 'student-week-schedule-item inactive'}>
                <span className="schedule-day-label">{day.label}:</span>
                <span className="schedule-time">{day.time || '-'}</span>
              </div>
            ))}
          </div>
          <div className={hasRemedialSchedule ? 'student-remedial-schedule active' : 'student-remedial-schedule inactive'}>
            <span className="schedule-day-label">보강:</span>
            <span className="schedule-time">{remedialSchedule}</span>
          </div>
        </div>

        <section className="admin-detail-card">
          <div className="admin-detail-header">
            <h2>내 정보</h2>
            <span>학생</span>
          </div>

          <div className="admin-detail-meta">
            <p>
              <strong>이메일:</strong> {editForm.email || '-'}
            </p>
            <p>
              <strong>이름:</strong> {editForm.name || '-'}
            </p>
          </div>

          <form className="auth-form admin-edit-form" onSubmit={handleSave}>
            <label className="auth-field">
              <span>비밀번호 (변경 시 입력)</span>
              <input
                type="password"
                value={editForm.password}
                readOnly={!isPasswordEditing}
                onClick={handlePasswordFieldClick}
                onChange={(event) => {
                  if (!isPasswordEditing) {
                    return;
                  }
                  handleFieldChange('password', event.target.value);
                }}
                placeholder={isPasswordEditing ? '새 비밀번호를 입력하세요' : '클릭해서 비밀번호 수정 여부를 선택하세요'}
              />
            </label>

            <label className="auth-field">
              <span>소속학원명</span>
              <input
                type="text"
                value={editForm.academyname}
                onChange={(event) => handleFieldChange('academyname', event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>이름</span>
              <input
                type="text"
                value={editForm.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>학생번호</span>
              <input
                type="text"
                value={editForm.tauserId}
                readOnly
                className="admin-readonly-field"
              />
            </label>

            <label className="auth-field">
              <span>핸드폰 번호(학생)</span>
              <input
                type="tel"
                value={editForm.phone}
                onChange={(event) => handleFieldChange('phone', event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>핸드폰 번호(보호자)</span>
              <input
                type="tel"
                value={editForm.parentPhone}
                onChange={(event) => handleFieldChange('parentPhone', event.target.value)}
                required
              />
            </label>

            <label className="auth-field">
              <span>학교 소재지역</span>
              <select
                value={editForm.location}
                onChange={(event) => handleStudentLocationChange(event.target.value)}
                required
              >
                <option value="">학교 소재지역을 선택하세요</option>
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </label>

            <label className="auth-field">
              <span>학교</span>
              <select
                value={editForm.schoolCode}
                onChange={(event) => handleStudentSchoolChange(event.target.value)}
                disabled={!editForm.location}
                required
              >
                <option value="">학교를 선택하세요</option>
                {filteredSchools.map((school) => (
                  <option key={`${school.code}-${school.schoolName}`} value={school.code}>
                    {school.schoolName}
                  </option>
                ))}
              </select>
            </label>

            <label className="auth-field">
              <span>반편성</span>
              <input
                type="text"
                value={editForm.class}
                onChange={(event) => handleFieldChange('class', event.target.value)}
              />
            </label>

            {pageMessage ? <p className="auth-inline-message">{pageMessage}</p> : null}

            <div className="admin-edit-actions">
              <button type="submit" className="auth-primary-button admin-edit-save-button" disabled={isSaving}>
                {isSaving ? '수정 중...' : '회원정보 수정 저장'}
              </button>
            </div>
          </form>
        </section>

      </section>
    </main>
  );
};

export default HomePage;
